import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import gameFormatService from '../../features/gameFormat/gameFormatService';

import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Paper,
  TextField,
  Typography,
  useTheme
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/DeleteOutline';
import PrimaryButton from '../ui/buttons/primaryButton';
import { generateTeamName } from '../../helpers/teams';

import { updateEventTeam } from '../../features/event/eventSlice';

// Replaces an entire team's roster in one action (e.g. a team withdraws and
// a substitute team takes its slot). Unlike editing individual players one
// at a time, this is framed as a deliberate "swap": the old lineup is shown
// read-only, and a brand new lineup must be built from scratch. The team's
// id/slot is kept the same, so it cascades to every game and bracket
// position the team already occupies (via the same update path used for
// regular team edits), and its status is reset to Active.
const SwapTeamDialog = ({ open, onClose, team }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const event = useSelector((state) => state.event.event);
  const updatingTeam = useSelector((state) => state.event.updatingTeam);

  const [players, setPlayers] = useState([]);
  const [newMembers, setNewMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setNewMembers([]);
      setSelectedMember(null);
      setError('');
    }
  }, [open, team]);

  useEffect(() => {
    if (event?.players) {
      setPlayers(
        event.players.map((player) => ({
          label: `${player.name} ${player.surname}`,
          value: player.id
        }))
      );
    }
  }, [event]);

  const handleAddMember = (member) => {
    if (member) {
      const player = event.players.find((p) => p.id === member.value);
      setSelectedMember(player);
    }
  };

  const handleOnAdd = (keyEvent) => {
    if (keyEvent.code === 'Enter' && selectedMember) {
      setError('');
      const limit = gameFormatService.getGameTypeLimit(event.gameFormat.gameType) / 2;

      const existsInList = newMembers.some((m) => m.id === selectedMember.id);
      const limitReached = newMembers.length >= limit;

      if (!existsInList && !limitReached) {
        setNewMembers((prev) => [...prev, selectedMember]);
        setSelectedMember(null);
      } else if (existsInList) {
        setError('Already in list.');
      } else if (limitReached) {
        setError('Limit reached.');
      }
    }
  };

  const handleRemoveMember = (player) => {
    setNewMembers((prev) => prev.filter((m) => m.id !== player.id));
  };

  const handleClose = () => {
    onClose();
  };

  const handleSwap = async () => {
    if (newMembers.length === 0) {
      setError('Add at least one player to the new lineup.');
      return;
    }

    const newMemberIds = new Set(newMembers.map((member) => member.id));
    const keptReserves = (Array.isArray(team.reserves) ? team.reserves : []).filter(
      (reserve) => !newMemberIds.has(reserve.id)
    );

    const updatedTeam = {
      id: team.id,
      members: newMembers,
      reserves: keptReserves,
      name: generateTeamName(event.gameFormat.gameType, newMembers),
      groupId: team.groupId || null,
      status: 'Active',
      statusReason: null
    };

    await dispatch(updateEventTeam({ eventId: event.id, team: updatedTeam })).unwrap();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Swap Team</DialogTitle>
      <DialogContent>
        {team && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: theme.palette.background.default }}>
              <Typography variant="caption" color="text.secondary">
                Current lineup (will be replaced)
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {team.name}
              </Typography>
              {Array.isArray(team.reserves) && team.reserves.length > 0 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 1 }}>
                  Reserves kept:{' '}
                  {team.reserves
                    .map((reserve) =>
                      [reserve.name, reserve.surname].filter(Boolean).join(' ')
                    )
                    .join(', ')}
                  . They do not play until promoted.
                </Typography>
              ) : null}
            </Box>

            <Autocomplete
              disablePortal
              options={players}
              onChange={(evt, value) => handleAddMember(value)}
              onKeyUp={(evt) => handleOnAdd(evt)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Start Typing"
                  helperText={error || 'Press Enter to add'}
                  color={error ? 'error' : 'info'}
                />
              )}
            />

            <Paper
              sx={{
                backgroundColor: theme.palette.background.default,
                minHeight: '120px'
              }}>
              <List>
                {newMembers.length > 0 ? (
                  newMembers.map((member) => (
                    <ListItem
                      key={member.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => handleRemoveMember(member)}>
                          <DeleteIcon />
                        </IconButton>
                      }>
                      {member.name} {member.surname}
                    </ListItem>
                  ))
                ) : (
                  <Typography m={2} variant="caption">
                    Build the new lineup for this team...
                  </Typography>
                )}
              </List>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={updatingTeam}>
          Cancel
        </Button>
        <PrimaryButton onClick={handleSwap} variant="contained" disabled={updatingTeam}>
          Confirm Swap
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default SwapTeamDialog;
